import { Modal, Button, Group, Stack, NumberInput, Grid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { StableKey } from 'app/models/common-types';
import { overrideSubTradedFacilityFee, removeOverriddenSubTradedFacilityFee } from 'app/state/ducks/traded-facilities/thunks';
import TextInput from 'app/views/components/inputs/TextInput'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'
import IonIcon from '@reacticons/ionicons';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';


interface Props {
    fee?: any | null;
    opened: boolean;
    close: () => void;
    reopen: () => void;
    facilityId: StableKey
    subTradeId: StableKey
}

function OverrideSubTradeFacilityFeeModal({ opened, close, fee, subTradeId, facilityId, reopen }: Props) {
    const dispatch = useDispatch();
    const [showOverriddenDeleteModal, setShowOverriddenDeleteModal] = useState(false);

    const form = useForm({
        initialValues: {
            // Show the positive magnitude, matching the table (which displays |amount|).
            overrideAmount: Math.abs(Number(fee?.overrideAmount ?? 0)),
            reason: fee?.reason ?? '',
        },

        validate: {
            overrideAmount: (value) =>
                value === null || value === undefined || (value as any) === '' ? 'Amount is required' : value < 0 ? 'Amount cannot be negative' : null,
            reason: (value) =>
                !value ? 'Reason is required' : null,
        },
        validateInputOnChange: true,
        validateInputOnBlur: true,
    });

    useEffect(() => {
        form.setValues({
            overrideAmount: Math.abs(Number(fee?.overrideAmount ?? 0)),
            reason: fee?.reason ?? '',
        })
    }, [opened, fee])

    const handleDelete = async () => {
        const response: any = await dispatch(removeOverriddenSubTradedFacilityFee(facilityId.id, subTradeId.id, fee.type));
        if (response?.success) {
            SuccessNotification({ title: 'Removed Overridden Fee Amount' });
        } else {
            ErrorNotification({
                title: 'Remove Overridden Fee Amount Failed',
                message: response.payload ?? 'Check data and try again',
            });
        }
        setShowOverriddenDeleteModal(false);
    };

    // Submit handler
    const handleSubmit = async (values: typeof form.values) => {
        // The table shows each fee as a positive magnitude with the counterparty flipped when the
        // calculated amount is negative. Keep the override in that SAME displayed direction by
        // storing it with the calculated fee's sign, so a fee shown as "Buyer" stays Buyer.
        const calcAmount = fee?.otherAmount != null && fee?.otherAmount !== ''
            ? Number(fee.otherAmount)
            : Number(fee?.overrideAmount ?? 0)
        const signedOverride = (calcAmount < 0 ? -1 : 1) * Number(values.overrideAmount)
        const result: any = await dispatch(overrideSubTradedFacilityFee({
            feeType: fee.type,
            overrideAmount: signedOverride,
            reason: values.reason,
            subTradeKey: subTradeId,
            facilityKey: facilityId,

        }))
        if (result && result.success) {
            SuccessNotification({ title: 'Fee Overridden successfully', message: 'Fee overridden successfully' })
        }
        else {
            ErrorNotification({ title: 'Failed to override fee', message: result?.payload ?? 'Failed to override fee' })
        }
        // Close the modal upon successful submission
        close();
    };

    return (
        <>
            <Modal
                opened={opened}
                onClose={close}
                centered
                withCloseButton={false}
                size="70%"
                className="modal-body create-new-form fit-content-modal"

            >
                <Stack w="100% !important" >
                    <div className="add-modal-fee" style={{ width: '100%' }}>
                        <div className="add-modal-fee-title-header">
                            <div className="add-modal-fee-title">
                                <div className="add-modal-fee-title-frame">
                                    <div className="add-modal-fee-title-text">Override {fee?.type ?? ''} Fee</div>
                                </div>
                            </div>
                            <div className="add-modal-fee-header-actions">
                                <div className="add-modal-fee-title-actions-expand">
                                    <Button
                                        onClick={close}
                                        variant="subtle"
                                        style={{
                                            padding: 0,
                                            background: 'transparent',
                                            height: 30,
                                            width: 30,
                                            minWidth: 'auto',
                                        }}
                                        aria-label="Close modal"
                                    >
                                        <CloseIcon style={{ width: '30px' }} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="content" style={{ width: '100%' }}>
                            <form onSubmit={form.onSubmit(handleSubmit)}>
                                <div className="create-new">
                                    <Grid style={{ minHeight: 'fit-content' }} gutter="md">
                                        <Grid.Col span={4}><NumberInput
                                            label="Amount"
                                            precision={2}
                                            placeholder="Enter Amount"
                                            parser={value =>
                                                value ? value.replace(/\$\s?|(,*)/g, '') : ''
                                            }
                                            formatter={value =>
                                                !Number.isNaN(parseFloat(value ?? ''))
                                                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                    : ''
                                            }
                                            {...form.getInputProps('overrideAmount')}
                                        />
                                        </Grid.Col>

                                        {/* Paid To */}

                                        <Grid.Col span={fee?.otherAmount ? 6 : 8}> <TextInput
                                            label="Reason"
                                            placeholder="Reason"
                                            {...form.getInputProps('reason')}
                                        />
                                        </Grid.Col>
                                        {fee?.otherAmount &&
                                            <Grid.Col span={2} style={{ marginTop: '20px' }}>
                                                <Group noWrap w="100%" mt="10px" position="center" onClick={() => { setShowOverriddenDeleteModal(true); close() }} style={{ cursor: 'pointer' }} >
                                                    <IonIcon
                                                        name="trash-outline"
                                                        className="click-ion-icon"
                                                        style={{ width: '25px', height: '25px' }}
                                                    />
                                                    <span
                                                        style={{
                                                            color: "#476DB9",
                                                            fontFamily: "Inter",
                                                            fontSize: "18px",
                                                            fontStyle: "normal",
                                                            fontWeight: "400",
                                                            lineHeight: "normal",
                                                        }}
                                                    >
                                                        Remove
                                                    </span>
                                                </Group>

                                            </Grid.Col>}
                                    </Grid>
                                    {/* Action buttons */}
                                    <Group position="right" mt="md">
                                        <Button className='add-modal-fee-cancel-button' onClick={close}>
                                            <div className="add-modal-fee-cancel-button-text"> Cancel </div>
                                        </Button>
                                        <Button disabled={!form.isValid()} className='add-modal-fee-save-button' type="submit"><div className='add-modal-fee-save-button-text'>Save</div></Button>
                                    </Group>
                                </div>
                            </form>
                        </div>
                    </div>

                </Stack>
            </Modal >
            {showOverriddenDeleteModal &&
                <DeleteConfirmation
                    onConfirm={() => handleDelete()}
                    onCancel={() => { setShowOverriddenDeleteModal(false); reopen() }}
                    message="Are you sure you want to delete this override amount?"
                    title="Delete Override"
                />
            }
        </>

    );
}

export default OverrideSubTradeFacilityFeeModal;